import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, fileUrl, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Lock, ImageOff, Truck } from "lucide-react";
import { Seo } from "../lib/seo";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [shipping, setShipping] = useState({ indirizzo: "", civico: "", cap: "", comune: "", provincia: "", nazione: "Italia", note: "" });
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  const threshold = settings?.free_shipping_threshold ?? 60;
  const shippingCost = subtotal >= threshold ? 0 : (settings?.shipping_cost ?? 6);
  const total = subtotal + shippingCost;
  const dmin = settings?.delivery_days_min ?? 2;
  const dmax = settings?.delivery_days_max ?? 5;

  if (items.length === 0) { navigate("/carrello"); return null; }

  const setC = (k, v) => setCustomer((f) => ({ ...f, [k]: v }));
  const setS = (k, v) => setShipping((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { toast.error("Devi accettare i termini e le condizioni di vendita"); return; }
    if (!privacy) { toast.error("Devi accettare l'informativa privacy"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer, shipping, accept_terms: terms, accept_privacy: privacy,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <Seo title="Checkout | Herbalife Rossano" description="Completa il tuo ordine come ospite." noindex />
      <h1 className="text-3xl font-extrabold font-display text-slate-900">Checkout</h1>
      <p className="mt-1 text-slate-500">Nessuna registrazione richiesta — acquista come ospite.</p>
      <form onSubmit={submit} className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 rounded-2xl border-slate-200">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Dati cliente</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <F l="Nome *"><Input required value={customer.nome} onChange={(e) => setC("nome", e.target.value)} data-testid="co-nome" /></F>
              <F l="Cognome *"><Input required value={customer.cognome} onChange={(e) => setC("cognome", e.target.value)} data-testid="co-cognome" /></F>
              <F l="Email *"><Input type="email" required value={customer.email} onChange={(e) => setC("email", e.target.value)} data-testid="co-email" /></F>
              <F l="Telefono *"><Input required value={customer.telefono} onChange={(e) => setC("telefono", e.target.value)} data-testid="co-telefono" /></F>
            </div>
          </Card>
          <Card className="p-6 rounded-2xl border-slate-200">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Indirizzo di spedizione</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <F l="Indirizzo *" full><Input required value={shipping.indirizzo} onChange={(e) => setS("indirizzo", e.target.value)} data-testid="co-indirizzo" /></F>
              <F l="Numero civico *"><Input required value={shipping.civico} onChange={(e) => setS("civico", e.target.value)} data-testid="co-civico" /></F>
              <F l="CAP *"><Input required value={shipping.cap} onChange={(e) => setS("cap", e.target.value)} data-testid="co-cap" /></F>
              <F l="Comune *"><Input required value={shipping.comune} onChange={(e) => setS("comune", e.target.value)} data-testid="co-comune" /></F>
              <F l="Provincia *"><Input required value={shipping.provincia} onChange={(e) => setS("provincia", e.target.value)} data-testid="co-provincia" /></F>
              <F l="Nazione *"><Input required value={shipping.nazione} onChange={(e) => setS("nazione", e.target.value)} data-testid="co-nazione" /></F>
              <F l="Note per la consegna (facoltative)" full><Textarea value={shipping.note} onChange={(e) => setS("note", e.target.value)} data-testid="co-note" /></F>
            </div>
          </Card>
        </div>

        <Card className="p-6 rounded-2xl border-slate-200 h-fit">
          <h3 className="font-display font-bold text-lg text-slate-900">Il tuo ordine</h3>
          <div className="mt-4 space-y-3 max-h-52 overflow-y-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex items-center gap-3 text-sm">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">
                  {fileUrl(it.image_url) ? <img src={fileUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-emerald-200" />}
                </div>
                <span className="flex-1 text-slate-600">{it.name} ×{it.quantity}</span>
                <span className="font-medium">{formatPrice(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotale</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Spedizione</span><span>{shippingCost === 0 ? "Gratuita" : formatPrice(shippingCost)}</span></div>
            <div className="flex justify-between font-bold text-lg text-slate-900"><span>Totale</span><span data-testid="checkout-total">{formatPrice(total)}</span></div>
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50/70 border border-emerald-100 p-3 text-xs text-emerald-800" data-testid="checkout-delivery-note">
            <Truck className="w-4 h-4 mt-0.5 shrink-0" />
            <span>La consegna avviene in <strong>{dmin}-{dmax} giorni lavorativi</strong> dal buon esito del pagamento.</span>
          </div>
          <label className="mt-4 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox checked={terms} onCheckedChange={setTerms} className="mt-0.5" data-testid="co-terms" />
            <span>Accetto i <Link to="/termini" className="text-primary underline">termini e condizioni</Link> di vendita *</span>
          </label>
          <label className="mt-2 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5" data-testid="co-privacy" />
            <span>Accetto l'<Link to="/privacy" className="text-primary underline">informativa privacy</Link> *</span>
          </label>
          <Button type="submit" disabled={loading} className="w-full mt-5 rounded-full py-6" data-testid="checkout-pay-btn"><Lock className="w-4 h-4 mr-2" /> {loading ? "Reindirizzamento..." : "Paga in sicurezza"}</Button>
          <p className="mt-3 text-xs text-slate-400 text-center">Pagamento sicuro con Stripe. I dati della carta non vengono memorizzati sul nostro sito.</p>
        </Card>
      </form>
    </div>
  );
}

function F({ l, children, full }) {
  return <div className={full ? "sm:col-span-2" : ""}><Label className="mb-1.5 block">{l}</Label>{children}</div>;
}
